import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Heading,
  VStack,
  useToast,
  Flex,
  Text,
  InputGroup,
  InputRightElement,
  IconButton,
  Spinner,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { isValidEmail, getPasswordValidation } from '../../../utils/validation';
import { BACKEND_URL } from '../../../api/config';

function IssuerCreateMiddleman() {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const toast = useToast();

  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const res = await fetch(
          `${BACKEND_URL}/accounts/account-roles`,
          {
            credentials: 'include',
            cache: 'no-store',
          }
        );

        if (!res.ok) throw new Error();

        const data = await res.json();
        console.log("ROLES RESPONSE:", data);
        const rolesList = Array.isArray(data.accountRoles) ? data.accountRoles : [];

        setRoles(rolesList);

        if (rolesList.length > 0) {
          setRole(rolesList[0].name);
        }
      } catch {
        toast({
          title: 'Failed to load roles',
          description: 'Please refresh the page or try again later.',
          status: 'error',
          position: 'top',
        });
      } finally {
        setRolesLoading(false);
      }
    };

    fetchRoles();
  }, [toast]);


  const isEmailEmpty = username.length === 0;
  const isPasswordEmpty = password.length === 0;

  const passwordValidation = getPasswordValidation(password);
  const isPasswordValid = passwordValidation.isValid;

  const isFormValid =
    isValidEmail(username.trim()) &&
    isPasswordValid &&
    role &&
    !isSubmitting;


  const handleCreate = async () => {
    if (!isFormValid) return;

    setIsSubmitting(true);

    try {
      const res = await fetch(
        `${BACKEND_URL}/accounts/middleman/create`,
        {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            username: username.trim(),
            password,
            role,
          }),
        }
      );

      if (res.status === 401) {
        toast({
          title: 'Session expired',
          description: 'Please log in again.',
          status: 'error',
          position: 'top',
        });
        return;
      }

      if (!res.ok) {
        let message = 'Failed to create account';
        try {
          const data = await res.json();
          if (data?.message) message = data.message;
        } catch {
          // ignore
        }
        throw new Error(message);
      }

      toast({
        title: 'Child account created',
        description: 'Share the credentials securely with the user.',
        status: 'success',
        position: 'top',
      });

      setUsername('');
      setPassword('');
      setRole(roles[0]?.name || '');
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to create account',
        status: 'error',
        position: 'top',
      });
    } finally {
      setIsSubmitting(false);
    }
  };


  return (
    <Flex
      width="100%"
      minH="calc(100vh - 120px)"
      align="center"
      justify="center"
      px={4}
    >
      <Box
        bg="white"
        p={8}
        borderRadius="xl"
        boxShadow="lg"
        width="100%"
        maxW="480px"
      >
        <Heading size="md" mb={6} textAlign="center">
          Create Child Account
        </Heading>

        <VStack spacing={5}>
          <FormControl isRequired>
            <FormLabel>Email</FormLabel>
            <Input
              value={username}
              onChange={e => setUsername(e.target.value)}
              placeholder="user@example.com"
              isInvalid={!isEmailEmpty && !isValidEmail(username)}
            />
            <Text
              h="20px"
              color="red"
              fontSize="sm"
              visibility={
                !isEmailEmpty && !isValidEmail(username)
                  ? 'visible'
                  : 'hidden'
              }
            >
              Please enter a valid email address
            </Text>
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Password</FormLabel>

            <InputGroup>
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="Password"
                isInvalid={!isPasswordEmpty && !isPasswordValid}
              />
              <InputRightElement>
                <IconButton
                  variant="ghost"
                  icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label="Toggle password visibility"
                />
              </InputRightElement>
            </InputGroup>

            {!isPasswordEmpty && !isPasswordValid && (
              <Box
                mt={2}
                bg="red.50"
                border="1px solid"
                borderColor="red.200"
                borderRadius="md"
                p={2}
              >
                {passwordValidation.issues.map(issue => (
                  <Text key={issue} fontSize="sm" color="red.600">
                    • {issue}
                  </Text>
                ))}
              </Box>
            )}
          </FormControl>

          <FormControl isRequired>
            <FormLabel>Role</FormLabel>
            {rolesLoading ? (
              <Spinner size="sm" />
            ) : (
              <Select
                value={role}
                onChange={e => setRole(e.target.value)}
              >
                {roles.map(r => (
                  <option key={r.name} value={r.name}>
                    {r.name}
                  </option>
                ))}
              </Select>
            )}
          </FormControl>

          <Button
            bg="#007bff"
            color="white"
            width="100%"
            size="md"
            _hover={{ bg: '#2F6FE0' }}
            _active={{ bg: '#2A63C7' }}
            onClick={handleCreate}
            isDisabled={!isFormValid || rolesLoading}
            isLoading={isSubmitting}
          >
            Create Account
          </Button>
        </VStack>
      </Box>
    </Flex>
  );
}

export default IssuerCreateMiddleman;
